import { isLoggedInAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { ListingList } from 'app/components/listing-list';
import { MyOffersEmptyState } from 'app/components/my-offers-empty-state';
import { Tabs } from 'app/components/tabs';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { getSelectAllListingIds } from 'app/lib/listing-selection';
import {
  useBatchDeleteListings,
  useBatchDuplicateListings,
  useBatchHideListings,
  useBatchUpdateListingsPrice,
  useBatchUnhideListings,
  useAllListings,
  useGetOneLoadedListing,
} from 'app/network/listing';
import { useUser } from 'app/network/user';
import type { Listing } from 'app/types/listing';
import { ListingType, TransactionMode } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { isMobileNative, usePageTitle, useUISize, useAlignmentIfDesktop } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import Toast from 'react-native-toast-message';
import { withOptionalMetrics } from '../../utils/performance/with-metrics';
import {
  DASHBOARD_LAYOUT_DEFAULT_LAYOUT,
  DASHBOARD_LAYOUT_DEFAULT_WITH_MESSAGE_FOOTER,
  withDashboardLayout,
} from 'app/hooks/with-dashboard-layout';
import { useDeviceHeight } from 'app/hooks/use-device-size';
import { getMyListingsOffersFilters, getMyListingsRequestsFilters } from 'app/network/listing-query-filters';
import { BonificationPrompt } from 'app/screens/billboard/all-listings/components/bonification-banner';
import { ListingBulkActions } from './my-listings/listing-bulk-actions';
import { ListingBulkEditDialogs } from './my-listings/listing-bulk-edit-dialogs';
import { ListingsSelectionAccessory } from './my-listings/listings-selection-accessory';
import { ListingsSelectionHeader } from './my-listings/listings-selection-header';
import { ManagementOfferCard } from './my-listings/management-offer-card';
import { theme } from './my-listings/shared';

const ALL_TRANSACTION_MODES = 'all' as const;
type TransactionModeTab = TransactionMode | typeof ALL_TRANSACTION_MODES;

const getCountLabel = (count?: number) => (count === undefined ? undefined : count.toString());

function useOfferListingCount({
  enabled,
  transactionMode,
  userId,
}: {
  enabled: boolean;
  transactionMode?: TransactionMode;
  userId?: string;
}) {
  const filters = useMemo(
    () => ({
      type: ListingType.offer,
      userId,
      take: 1,
      transactionMode,
    }),
    [transactionMode, userId],
  );
  return useAllListings(filters, `MyListings-${transactionMode ?? ALL_TRANSACTION_MODES}-Count`, {
    enabled,
    populateListingDetailCache: false,
  });
}

export {
  getMyListingsCountFilters,
  getMyListingsOffersFilters,
  getMyListingsRequestsFilters,
} from 'app/network/listing-query-filters';

const MyListingsScreenComponent: NavigationScreen<'my-listings'> = ({ navigation, route }) => {
  const { t } = useScopedTranslation();
  const { data: user } = useUser();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { isSmallUI, isLargeUI } = useUISize();
  const [searchQuery, setSearchQuery] = useState('');
  const showTransactionModeTabs = isWhiteLabel(WhiteLabel.HauteGaspesie) && route.params.type === ListingType.offer;
  const [transactionModeTab, setTransactionModeTab] = useState<TransactionModeTab>(ALL_TRANSACTION_MODES);
  const transactionMode = transactionModeTab === ALL_TRANSACTION_MODES ? undefined : transactionModeTab;
  const { allCountQuery, lendCountQuery, sellCountQuery, giveCountQuery, transactionModeCounts } =
    useTransactionModeCounts(showTransactionModeTabs, user);

  const setPageTitle = usePageTitle('my-listings');
  useEffect(() => {
    setPageTitle(`title.${route.params.type as string}`);
  }, [route.params.type, setPageTitle]);

  const loanFeeAvailable = useMemo(
    () => isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags),
    [user?.featureFlags],
  );

  const selection = useListingSelection({ navigation, route, setSearchQuery });
  const {
    isSelecting,
    setIsSelecting,
    selectedRowsState,
    setListingForMenuAction,
    selectAll,
    setSelectAll,
    showActions,
    setShowActions,
    onSelectableListingIdsChange,
    onEdit,
    onSelectAll,
    onSearchQueryChange,
    showHideButton,
    showUnhideButton,
    canEditPrice,
  } = selection;

  const [filters, setFilters] = useState(
    route.params.type === ListingType.request
      ? getMyListingsRequestsFilters(isLoggedIn, user?.id, 25)
      : getMyListingsOffersFilters(isLoggedIn, user?.id, 25, showTransactionModeTabs ? transactionMode : undefined),
  );

  // On web hard-refresh, useUser() resolves asynchronously after the initial render,
  // so user?.id is undefined in the useState initializer above. Re-sync filters once
  // the user id becomes available so we don't accidentally show all users' listings.
  const syncFiltersToUser = useEffectEvent(() => {
    if (!user?.id) return;
    setFilters((prev) => {
      const nextTransactionMode = showTransactionModeTabs ? transactionMode : undefined;
      if (prev.userId === user.id && prev.type === route.params.type && prev.transactionMode === nextTransactionMode) {
        return prev;
      }
      return route.params.type === ListingType.request
        ? getMyListingsRequestsFilters(isLoggedIn, user.id, 25)
        : getMyListingsOffersFilters(isLoggedIn, user.id, 25, nextTransactionMode);
    });
  });
  useEffect(() => {
    const timeout = setTimeout(syncFiltersToUser, 0);
    return () => clearTimeout(timeout);
  }, [user?.id, route.params.type, isLoggedIn, showTransactionModeTabs, transactionMode]);

  // Refresh the tab counts after bulk actions. On web, also force-reload the list because invalidating it is not enough.
  const onSuccess = () => {
    if (showTransactionModeTabs) {
      void Promise.all([
        allCountQuery.refetch(),
        lendCountQuery.refetch(),
        sellCountQuery.refetch(),
        giveCountQuery.refetch(),
      ]);
    }
    if (!isMobileNative) {
      setFilters((f) => ({ ...f, __counter: f.__counter! + 1 }));
    }
  };

  const filtersWithSearch = useMemo(
    () => ({
      ...filters,
      search: searchQuery.trim(),
    }),
    [filters, searchQuery],
  );

  const syncDesktopFilters = useEffectEvent(() => {
    if (isSmallUI) return;

    setFilters((prev) => ({
      ...prev,
      type: route.params.type,
      userId: user?.id,
      isLoggedIn: isLoggedIn,
      transactionMode: showTransactionModeTabs ? transactionMode : undefined,
    }));
  });
  useEffect(() => {
    const timeout = setTimeout(syncDesktopFilters, 0);
    return () => clearTimeout(timeout);
  }, [route.params.type, user?.id, isLoggedIn, isSmallUI, showTransactionModeTabs, transactionMode]);

  const onTransactionModeChange = (value: string) => {
    const nextTransactionModeTab = value as TransactionModeTab;
    const nextTransactionMode = nextTransactionModeTab === ALL_TRANSACTION_MODES ? undefined : nextTransactionModeTab;
    setTransactionModeTab(nextTransactionModeTab);
    setFilters((prev) => ({ ...prev, transactionMode: nextTransactionMode }));
    selectedRowsState[1]([]);
    setSelectAll(false);
    setIsSelecting(false);
  };

  const bulkActions = useListingBulkActions({ navigation, selection, filtersWithSearch, onSuccess, t });
  const { onBlockDates, onChangeLoanFee, onHide, onUnhide, onDelete, onDuplicate, onEditPrice } = bulkActions;

  const [isEmpty, setIsEmpty] = useState(true);

  const [topShadowOpacity, setTopShadowOpacity] = useState(0);
  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const yPos = Math.max(event.nativeEvent.contentOffset.y, 0);
    setTopShadowOpacity(Math.min(0.06, (0.06 * yPos) / 30));
  }, []);

  const alignment = useAlignmentIfDesktop();
  const deviceHeight = useDeviceHeight();

  return (
    <>
      <Column height={isLargeUI ? deviceHeight * 0.9 : undefined} flex={isSmallUI ? 1 : undefined}>
        <Row
          paddingHorizontal="spacing.general.x4"
          paddingVertical="spacing.general.x4"
          backgroundColor="surface.default"
          shadowOpacity={topShadowOpacity}
          shadowRadius={10}
          shadowOffset={{ width: 0, height: 4 }}
          shadowColor="text.primary"
        >
          <Text fontWeight="700" style={{}} textAlign={alignment} variant="headerXSmall">
            {t(`title.${route.params.type as string}`)}
          </Text>
        </Row>

        {route.params.type === ListingType.offer ? <BonificationPrompt /> : null}

        {showTransactionModeTabs ? (
          <Tabs
            onPress={onTransactionModeChange}
            tabs={[
              {
                value: ALL_TRANSACTION_MODES,
                label: t('transactionModeTabs.all'),
                extraLabel: getCountLabel(transactionModeCounts[ALL_TRANSACTION_MODES]),
              },
              {
                value: TransactionMode.lend,
                label: t('transactionModeTabs.lend'),
                extraLabel: getCountLabel(transactionModeCounts[TransactionMode.lend]),
              },
              {
                value: TransactionMode.sell,
                label: t('transactionModeTabs.sell'),
                extraLabel: getCountLabel(transactionModeCounts[TransactionMode.sell]),
              },
              {
                value: TransactionMode.give,
                label: t('transactionModeTabs.give'),
                extraLabel: getCountLabel(transactionModeCounts[TransactionMode.give]),
              },
            ]}
            value={transactionModeTab}
          />
        ) : null}

        <Column flex={1}>
          <ListingList
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            searchAsYouType={false}
            showSearchBar
            showResultsCount
            resultsHeaderActions={
              <ListingsSelectionHeader
                route={route}
                isEmpty={isEmpty}
                isSelecting={isSelecting}
                isSmallUI={isSmallUI}
                t={t}
                selectAll={selectAll}
                onSelectAll={onSelectAll}
                onEdit={onEdit}
                isLargeUI={isLargeUI}
                selectedRowsState={selectedRowsState}
                setListingForMenuAction={setListingForMenuAction}
                setShowActions={setShowActions}
              />
            }
            ListEmptyComponent={
              showTransactionModeTabs ? <MyOffersEmptyState transactionMode={transactionModeTab} /> : undefined
            }
            accessory={
              <ListingsSelectionAccessory
                isSelecting={isSelecting}
                isSmallUI={isSmallUI}
                isLargeUI={isLargeUI}
                selectedRowsState={selectedRowsState}
                selectAll={selectAll}
                t={t}
                setListingForMenuAction={setListingForMenuAction}
                setShowActions={setShowActions}
              />
            }
            filters={filtersWithSearch}
            i18nKey={showTransactionModeTabs ? transactionModeTab : undefined}
            isEmptyState={[isEmpty, setIsEmpty]}
            isSelecting={isSelecting}
            onSelectableListingIdsChange={onSelectableListingIdsChange}
            onScroll={onScroll}
            showsVerticalScrollIndicator={isLargeUI}
            selectAll={selectAll}
            selectedRowsState={selectedRowsState}
            style={{ paddingTop: theme.spacing['spacing.general.x4'] }}
            onBlockDates={onBlockDates}
            onChangeLoanFee={onChangeLoanFee}
            onHide={onHide}
            onUnhide={onUnhide}
            onDelete={onDelete}
            setListingForMenuAction={setListingForMenuAction}
            showHighestCard={isLargeUI}
            showOffersBanner
            metricsLabel="my-listings-ListingList-Cp"
            card={route.params.type === ListingType.offer ? ManagementOfferCard : undefined}
            listFormat={route.params.type === ListingType.offer ? 'list' : undefined}
          />
        </Column>
      </Column>

      <ListingBulkActions
        setShowActions={setShowActions}
        showActions={showActions}
        t={t}
        user={user}
        onBlockDates={onBlockDates}
        onDuplicate={onDuplicate}
        canEditPrice={canEditPrice}
        onEditPrice={onEditPrice}
        loanFeeAvailable={loanFeeAvailable}
        onChangeLoanFee={onChangeLoanFee}
        showHideButton={showHideButton}
        onHide={onHide}
        showUnhideButton={showUnhideButton}
        onUnhide={onUnhide}
        onDelete={onDelete}
      />

      <ListingBulkEditDialogs actions={bulkActions} selectedRowsState={selectedRowsState} t={t} />
    </>
  );
};

const getMyListingsLayout = (isMobileUI: boolean) =>
  isMobileUI ? DASHBOARD_LAYOUT_DEFAULT_LAYOUT : 'simpleVerticalScrolling';
const getMyListingsMessageFooterVisibility = (isMobileUI: boolean) =>
  isMobileUI ? DASHBOARD_LAYOUT_DEFAULT_WITH_MESSAGE_FOOTER : true;

export const MyListingsScreen: NavigationScreen<'my-listings'> = withOptionalMetrics(
  'MyListingsScreen',
  withErrorBoundary(
    withDashboardLayout(MyListingsScreenComponent, getMyListingsLayout, getMyListingsMessageFooterVisibility),
  ),
);

function useTransactionModeCounts(showTransactionModeTabs: boolean, user: ReturnType<typeof useUser>['data']) {
  const allCountQuery = useOfferListingCount({
    enabled: showTransactionModeTabs && Boolean(user?.id),
    userId: user?.id,
  });
  const lendCountQuery = useOfferListingCount({
    enabled: showTransactionModeTabs && Boolean(user?.id),
    transactionMode: TransactionMode.lend,
    userId: user?.id,
  });
  const sellCountQuery = useOfferListingCount({
    enabled: showTransactionModeTabs && Boolean(user?.id),
    transactionMode: TransactionMode.sell,
    userId: user?.id,
  });
  const giveCountQuery = useOfferListingCount({
    enabled: showTransactionModeTabs && Boolean(user?.id),
    transactionMode: TransactionMode.give,
    userId: user?.id,
  });
  const transactionModeCounts = {
    [ALL_TRANSACTION_MODES]: allCountQuery.data?.pages[0]?.total,
    [TransactionMode.lend]: lendCountQuery.data?.pages[0]?.total,
    [TransactionMode.sell]: sellCountQuery.data?.pages[0]?.total,
    [TransactionMode.give]: giveCountQuery.data?.pages[0]?.total,
  };
  return { allCountQuery, lendCountQuery, sellCountQuery, giveCountQuery, transactionModeCounts };
}

function useListingSelection({
  navigation,
  route,
  setSearchQuery,
}: React.ComponentProps<NavigationScreen<'my-listings'>> & {
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
}) {
  const [isSelecting, setIsSelecting] = useState(false);

  const selectedRowsState = useState<Listing['id'][]>([]);

  const [listingForMenuAction, setListingForMenuAction] = useState<Listing['id'] | null>(null);

  const [selectAll, setSelectAll] = useState(false);
  const selectableListingIdsRef = useRef<Listing['id'][]>([]);
  const [showActions, setShowActions] = useState(false);

  const onSelectableListingIdsChange = useCallback((ids: Listing['id'][]) => {
    selectableListingIdsRef.current = ids;
  }, []);

  const onEdit = () => {
    setShowActions(false);
    selectedRowsState[1]([]);
    setIsSelecting(!isSelecting);
    setSelectAll(false);
    setListingForMenuAction(null);
  };

  // When coming back to this screen after saving new reserved dates, we want to exit edit mode
  const exitSelectionFromRoute = useEffectEvent(() => {
    if (isSelecting) onEdit();
    navigation.setParams({ ...route.params, isSelecting: undefined });
  });
  useEffect(() => {
    if (route.params.isSelecting === false) {
      const timeout = setTimeout(exitSelectionFromRoute, 0);
      return () => clearTimeout(timeout);
    }
  }, [route.params.isSelecting]);

  const onSelectAll = (isSelectAllPartial: boolean) => {
    const nextSelectedIds = getSelectAllListingIds({
      currentItemIds: selectableListingIdsRef.current,
      isSelectAllPartial,
      selectAll,
    });

    selectedRowsState[1](nextSelectedIds);
    setSelectAll(!(selectAll || isSelectAllPartial));
  };

  const onSearchQueryChange: React.Dispatch<React.SetStateAction<string>> = (query) => {
    setSearchQuery(query);
    selectedRowsState[1]([]);
    setSelectAll(false);
  };

  const getListing = useGetOneLoadedListing();

  const selectedListings = useMemo(() => {
    const listings: Listing[] = [];
    const selectedIds = selectedRowsState[0];

    for (let index = 0; index < selectedIds.length; index += 1) {
      if (!(index in selectedIds)) continue;

      const listing = getListing(selectedIds[index]!);
      if (listing) listings.push(listing);
    }

    return listings;
  }, [selectedRowsState, getListing]);

  const showHideButton = useMemo(() => {
    return !!selectedListings.find((listing) => {
      const isInvisible = !listing.addresses?.length && !listing.groups?.length;
      return !isInvisible;
    });
  }, [selectedListings]);

  const showUnhideButton = useMemo(() => {
    return !!selectedListings.find((listing) => {
      // noinspection UnnecessaryLocalVariableJS
      const isInvisible = !listing.addresses?.length && !listing.groups?.length;
      return isInvisible;
    });
  }, [selectedListings]);

  const canEditPrice =
    selectedListings.length > 0 &&
    selectedListings.every((listing) => listing.transactionMode === TransactionMode.sell);

  return {
    isSelecting,
    setIsSelecting,
    selectedRowsState,
    listingForMenuAction,
    setListingForMenuAction,
    selectAll,
    setSelectAll,
    showActions,
    setShowActions,
    onSelectableListingIdsChange,
    onEdit,
    onSelectAll,
    onSearchQueryChange,
    showHideButton,
    showUnhideButton,
    canEditPrice,
  };
}

export function useListingBulkActions({
  navigation,
  selection,
  filtersWithSearch,
  onSuccess,
  t,
}: {
  navigation: React.ComponentProps<NavigationScreen<'my-listings'>>['navigation'];
  selection: ReturnType<typeof useListingSelection>;
  filtersWithSearch: NonNullable<Parameters<typeof useBatchHideListings>[0]>['invalidateFilters'];
  onSuccess: () => void;
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  const {
    selectedRowsState,
    listingForMenuAction,
    setShowActions,
    setListingForMenuAction,
    onEdit,
    setSelectAll,
    setIsSelecting,
  } = selection;
  const onBlockDates = () => {
    setShowActions(false);
    setListingForMenuAction(null);
    setTimeout(() => {
      navigation.navigate(
        'offer-availability',
        {
          listingId: listingForMenuAction ?? selectedRowsState[0].join(','),
        },
        { pop: true },
      );
    }, 300);
  };

  const onChangeLoanFee = () => {
    setShowActions(false);
    setTimeout(() => {
      navigation.navigate(
        'loan-fee',
        {
          listingId: listingForMenuAction ?? selectedRowsState[0].join(','),
        },
        { pop: true },
      );
    }, 300);
  };

  const { mutate: hideListings } = useBatchHideListings({ invalidateFilters: filtersWithSearch, onSuccess });
  const { mutate: unhideListings } = useBatchUnhideListings({ invalidateFilters: filtersWithSearch, onSuccess });
  const { mutate: deleteListings } = useBatchDeleteListings({ invalidateFilters: filtersWithSearch, onSuccess });
  const { mutate: duplicateListings, isPending: isDuplicatingListings } = useBatchDuplicateListings({
    invalidateFilters: filtersWithSearch,
    onSuccess,
  });
  const { mutate: updateListingsPrice } = useBatchUpdateListingsPrice({
    invalidateFilters: filtersWithSearch,
    onSuccess,
  });
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [price, setPrice] = useState('');

  const finishBulkEdit = () => {
    selectedRowsState[1]([]);
    setSelectAll(false);
    setIsSelecting(false);
  };

  const onDuplicate = () => {
    setShowActions(false);
    setListingForMenuAction(null);
    setDuplicateCount(1);
    setTimeout(() => setShowDuplicateModal(true), 300);
  };

  const onEditPrice = () => {
    setShowActions(false);
    setListingForMenuAction(null);
    setPrice('');
    setTimeout(() => setShowPriceModal(true), 300);
  };

  interface AlertPolyfill {
    visible: boolean;
    title: string;
    message?: string;
    buttons?: { text: string; style?: 'cancel' | 'destructive'; isPreferred?: boolean; onPress?: () => void }[];
  }
  const [alert, setAlert] = useState<AlertPolyfill>();

  const showAlert = (
    title: AlertPolyfill['title'],
    message?: AlertPolyfill['message'],
    buttons?: AlertPolyfill['buttons'],
  ) => setTimeout(() => setAlert({ title, message, buttons, visible: true }), 300);
  const hideAlert = () => setAlert((alertValue) => ({ ...alertValue, visible: false }) as AlertPolyfill);

  const onHide = () => {
    setShowActions(false);
    showAlert(t('hideAlert.title'), t('hideAlert.message'), [
      {
        text: t('hideAlert.cancelButton'),
        style: 'cancel',
      },
      {
        text: t('hideAlert.confirmButton'),
        isPreferred: true,
        onPress: () => {
          onEdit();
          hideListings(listingForMenuAction ? [listingForMenuAction] : selectedRowsState[0], {
            onSuccess: () => {
              Toast.show({
                type: 'success',
                position: 'bottom',
                bottomOffset: 100,
                text1: t('hideToast.title'),
                text2: t('hideToast.text'),
                visibilityTime: 5000,
              });
            },
          });
        },
      },
    ]);
  };

  const onUnhide = () => {
    setShowActions(false);
    showAlert(t('unhideAlert.title'), t('unhideAlert.message'), [
      {
        text: t('unhideAlert.cancelButton'),
        style: 'cancel',
      },
      {
        text: t('unhideAlert.confirmButton'),
        isPreferred: true,
        onPress: () => {
          onEdit();
          unhideListings(listingForMenuAction ? [listingForMenuAction] : selectedRowsState[0], {
            onSuccess: () => {
              Toast.show({
                type: 'success',
                position: 'bottom',
                bottomOffset: 100,
                text1: t('unhideToast.title'),
                text2: t('unhideToast.text'),
                visibilityTime: 5000,
              });
            },
          });
        },
      },
    ]);
  };

  const onDelete = () => {
    setShowActions(false);
    showAlert(t('deleteAlert.title'), t('deleteAlert.message'), [
      {
        text: t('deleteAlert.cancelButton'),
        style: 'cancel',
      },
      {
        text: t('deleteAlert.confirmButton'),
        isPreferred: true,
        style: 'destructive',
        onPress: () => {
          onEdit();
          deleteListings(listingForMenuAction ? [listingForMenuAction] : selectedRowsState[0], {
            onSuccess: () => {
              Toast.show({
                type: 'success',
                position: 'bottom',
                bottomOffset: 100,
                text1: t('deleteToast.title'),
                text2: t('deleteToast.text'),
                visibilityTime: 5000,
              });
            },
          });
        },
      },
    ]);
  };

  return {
    onBlockDates,
    onChangeLoanFee,
    onHide,
    onUnhide,
    onDelete,
    onDuplicate,
    onEditPrice,
    showDuplicateModal,
    setShowDuplicateModal,
    showPriceModal,
    setShowPriceModal,
    duplicateCount,
    setDuplicateCount,
    price,
    setPrice,
    isDuplicatingListings,
    duplicateListings,
    updateListingsPrice,
    finishBulkEdit,
    alert,
    hideAlert,
  };
}
